import type {
    SparkflowContextOptions,
    SpotbuildGeneratedIdsResponse,
} from '@thoughtspot/blink-context';
import {
    DEFAULT_SPOTBUILD_IDS,
    INTENT_PATH_CONTEXT_KEY,
    INTENT_PATH_NOT_APPLICABLE,
    PATH_SEQUENCE_CONTEXT_KEY,
} from '@thoughtspot/blink-context';
import { translate } from '@thoughtspot/i18n';

/** Fallback mapping used when the user has no generated spotbuild IDs. */
export const getDefaultSpotbuildMapping = (): SpotbuildGeneratedIdsResponse[] => [
    {
        function: 'NA',
        industry: 'NA',
        company_size: 'NA',
        onboarding_context: {
            [INTENT_PATH_CONTEXT_KEY]: INTENT_PATH_NOT_APPLICABLE,
            [PATH_SEQUENCE_CONTEXT_KEY]: INTENT_PATH_NOT_APPLICABLE,
        },
        liveboard: {
            id: DEFAULT_SPOTBUILD_IDS.LIVEBOARD,
            name: translate('sparkflow.defaultMapping.liveboardName'),
        },
        data_sources: [
            {
                model: {
                    id: DEFAULT_SPOTBUILD_IDS.MODEL,
                    name: translate('sparkflow.defaultMapping.modelName'),
                },
                tables: DEFAULT_SPOTBUILD_IDS.TABLES.map(id => ({
                    id,
                    name: '',
                })),
            },
        ],
    },
];

/**
 * Reset the sparkflow global context when the user navigates to a different page
 * When in agent-selection screen and we manually route to different page, by
 * skipping the pinboard edit page then it has to be ensured that the global banner
 * and free trial banner shows again
 * Currently the isLoadingIndicatorVisible is to false if we go in pinboard page
 * in edit mode
 * Similarly if user is in pinboard page and then manually routes to different page,
 * then it has to be ensured that the global banner and free trial banner shows again
 * @param pathname - The pathname of the current page
 * @param isLoadingIndicatorVisible - Whether the loading indicator is visible
 * @param hideSparkflowBanner - Whether the global banner is hidden
 * @param updateSparkflowGlobalContext - The function to update the sparkflow global context
 *
 * Extracted from sparkflow-banner.container.tsx to break the transitive static
 * import of GlobalBanner → global-banners.container → lottie-react that was
 * pulling lottie-web (663 KB) into the main bundle.
 */
export const resetSparkflowGlobalContext = (
    pathname: string,
    hideSparkflowBanner: boolean,
    isLoadingIndicatorVisible: boolean,
    updateSparkflowGlobalContext: (
        context: Partial<SparkflowContextOptions>,
    ) => void,
) => {
    if (
        !pathname.includes('/sparkflow/pinboard') &&
        (hideSparkflowBanner || isLoadingIndicatorVisible)
    ) {
        updateSparkflowGlobalContext({
            isLoadingIndicatorVisible: false,
            hideSparkflowBanner: false,
        });
    }
};
